import { useEffect, useMemo, useRef, type ReactNode } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { ScrollArea } from "../ui/scroll-area";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { cn } from "~/lib/utils";

const SELECT_COLUMN_ID = "select";
const SELECT_COLUMN_WIDTH = "2rem";
const SELECT_HEADER_CLASS = "w-8";
const ROW_HEIGHT_ESTIMATE = 33;

export interface FilesTableColumnLayout {
  readonly width: string;
  readonly headerClassName: string;
}

export interface FilesTableSelectLabels {
  readonly all: string;
  readonly one: string;
}

export interface FilesTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  layout: Record<string, FilesTableColumnLayout>;
  getRowId: (row: TData) => string;
  getSubRows?: (row: TData) => TData[] | undefined;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  selectionMode: boolean;
  selectLabels: FilesTableSelectLabels;
  virtualizerEnabled: boolean;
  scrollResetKey: string;
  placeholder?: ReactNode;
}

function selectColumn<TData>(labels: FilesTableSelectLabels): ColumnDef<TData, unknown> {
  return createColumnHelper<TData>().display({
    id: SELECT_COLUMN_ID,
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onCheckedChange={(checked) => table.toggleAllRowsSelected(checked === true)}
        aria-label={labels.all}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(checked) => row.toggleSelected(checked === true)}
        aria-label={labels.one}
      />
    ),
  });
}

export function FilesTable<TData>({
  data,
  columns,
  layout,
  getRowId,
  getSubRows,
  sorting,
  onSortingChange,
  rowSelection,
  onRowSelectionChange,
  selectionMode,
  selectLabels,
  virtualizerEnabled,
  scrollResetKey,
  placeholder,
}: FilesTableProps<TData>) {
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const allColumns = useMemo(
    () => (selectionMode ? [selectColumn<TData>(selectLabels), ...columns] : columns),
    [columns, selectLabels, selectionMode],
  );

  const table = useReactTable({
    data,
    columns: allColumns,
    state: { sorting, rowSelection, expanded: true },
    onSortingChange,
    onRowSelectionChange,
    getRowId,
    ...(getSubRows ? { getSubRows } : {}),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableRowSelection: true,
    enableSubRowSelection: false,
  });

  const rows = table.getRowModel().rows;
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => viewportRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: 12,
    // Disabling while the panel is closed drops the virtualizer's cached scroll
    // offset and observers; otherwise a scrollTop reset on the hidden keepMounted
    // popup is missed and the reopened list renders shifted by a stale offset.
    enabled: virtualizerEnabled,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0]!.start : 0;
  const paddingBottom =
    virtualRows.length > 0 ? rowVirtualizer.getTotalSize() - virtualRows.at(-1)!.end : 0;

  useEffect(() => {
    rowVirtualizer.scrollToOffset(0);
  }, [scrollResetKey, rowVirtualizer]);

  const columnLayout = (columnId: string): FilesTableColumnLayout | undefined =>
    columnId === SELECT_COLUMN_ID
      ? { width: SELECT_COLUMN_WIDTH, headerClassName: SELECT_HEADER_CLASS }
      : layout[columnId];

  const colgroup = (
    <colgroup>
      {table.getVisibleLeafColumns().map((column) => (
        <col key={column.id} style={{ width: columnLayout(column.id)?.width }} />
      ))}
    </colgroup>
  );

  const showTable = placeholder === undefined && rows.length > 0;

  return (
    <>
      {showTable ? (
        <div className="shrink-0">
          <table className="w-full table-fixed text-xs">
            {colgroup}
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "h-8 px-2 text-[11px] font-semibold tracking-wide text-muted-foreground/60 uppercase",
                        columnLayout(header.column.id)?.headerClassName,
                      )}
                    >
                      {header.column.getCanSort() ? (
                        <button
                          type="button"
                          className="flex w-full items-center gap-0.5 hover:text-foreground/80"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() === "asc" ? (
                            <ChevronUpIcon className="size-3.5 shrink-0" strokeWidth={2.5} />
                          ) : header.column.getIsSorted() === "desc" ? (
                            <ChevronDownIcon className="size-3.5 shrink-0" strokeWidth={2.5} />
                          ) : null}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
          </table>
        </div>
      ) : null}

      <ScrollArea className="min-h-0 flex-1" viewportRef={viewportRef}>
        {showTable ? (
          <table className="w-full table-fixed caption-bottom text-xs">
            {colgroup}
            <TableBody>
              {paddingTop > 0 ? <tr aria-hidden style={{ height: paddingTop }} /> : null}
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index]!;
                return (
                  <TableRow
                    key={row.id}
                    ref={rowVirtualizer.measureElement}
                    data-index={virtualRow.index}
                    className="group/row"
                    data-state={row.getIsSelected() ? "selected" : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn("px-2 py-1.5", cell.column.id === "modified" && "relative")}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
              {paddingBottom > 0 ? <tr aria-hidden style={{ height: paddingBottom }} /> : null}
            </TableBody>
          </table>
        ) : (
          placeholder
        )}
      </ScrollArea>
    </>
  );
}
